import React from "react";
import PropTypes from "prop-types";
import ImmutablePropTypes from "react-immutable-proptypes";
import classnames from "classnames";
import { getResource, getResources } from "selectors/resources";
import { Client, Language } from "resourceModels";
import Button from "components/Common/Button";
import SvgIcon from "components/Common/SvgIcon";
import { introsDeleteLanguage } from "components/Pages/Responses/ResponsesActionBar/actions";
import { setState } from "actions";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import colors from "stylesheets/utilities/colors.scss";
import "./style.scss";

const blockName = "MultilingualInputLanguagesDialog";

class MultilingualInputLanguagesDialog extends React.Component {
  isPrimaryLanguage(code) {
    const {
      /** @type Client */
      client
    } = this.props;

    return client.language === code;
  }

  hasInvalidField(code) {
    return this.props.invalidFields.includes(code);
  }

  render() {
    const {
      /** @type Client */
      client,
      languages,
      activeLanguage
    } = this.props;

    return (
      <div className={blockName}>
        <div className={`${blockName}__languages`}>
          {this.props.enabledLanguages && (
            <div className={`${blockName}__languages__enabled-languages`}>
              <div className={`${blockName}__languages__title`}>
                Custom Translations
              </div>
              {this.props.enabledLanguages.map(language => (
                // Not going to have a keyboard handler
                <div // eslint-disable-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus, max-len
                  role="button"
                  className={classnames(
                    `${blockName}__languages__language-button--container`, {
                      [`${blockName}__languages__language-button--selected`]: (
                        this.props.activeLanguage === language.code
                      )
                    }
                  )}
                  key={language.code}
                  onClick={() => {
                    if (this.isPrimaryLanguage(language.code)) {
                      this.props.onClick(language);
                      this.props.closeDialog();
                    }
                  }}
                >
                  <Button
                    text={language.englishName}
                    title={language.englishName}
                    customClassName={classnames(
                      `${blockName}__languages__language-button`, {
                        [`${blockName}__languages__language-button--selected`]: this.props.activeLanguage === language.code,
                        [`${blockName}__languages__language-button--available`]: client.language === language.code && this.props.activeLanguage !== language.code,
                        [`${blockName}__languages__language-button--invalid`]: this.hasInvalidField(language.code)
                      }
                    )}
                    key={language.code}
                    secondIcon={(() => {
                      if (this.hasInvalidField(language.code)) {
                        return "Warning";
                      }

                      return this.isPrimaryLanguage(language.code) ? "Lock" : "";
                    })()}
                    fillColor={(() => {
                      if (this.hasInvalidField(language.code)) {
                        return colors.colorUIWarn;
                      }

                      return activeLanguage === language.code ? colors.colorUIPrimary : "";
                    })()}
                    onClick={() => {
                      if (!this.isPrimaryLanguage(language.code)) {
                        this.props.onClick(language);
                        this.props.closeDialog();
                      }
                    }}
                    left
                    size="small"
                    clear
                  />
                  {!this.isPrimaryLanguage(language.code) && (
                    <Button
                      customClassName={`${blockName}__languages__language-button__delete`}
                      key={`delete-${language.code}`}
                      title="Delete Language"
                      icon="CircleRemoveAlternate"
                      fillColor={colors.colorUIBad}
                      onClick={() => this.props.deleteLanguage(language.code)}
                      clear
                    />
                  )}
                  {this.props.activeLanguage === language.code && (
                    <SvgIcon
                      key={`active-${language.code}`}
                      customClassName={`${blockName}__languages__language-button__active`}
                      icon="Checkmark"
                      fillColor={colors.colorUIPrimary}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          <div className={`${blockName}__languages__available-languages`}>
            <div className={`${blockName}__languages__title`}>
              Available Languages
            </div>
            {this.props.availableLanguages.map(
              language => (
                // This needs to be changed to Button component
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <div
                  role="button"
                  tabIndex={0}
                  className={`${blockName}__languages__language-button--container`}
                  key={language.code}
                  onClick={() => {
                    this.props.onLanguageClick(language.code);
                    this.props.onClick(language);
                    this.props.closeDialog();
                  }}
                >
                  <Button
                    text={language.englishName}
                    title={language.englishName}
                    customClassName={classnames(
                      `${blockName}__languages__language-button`,
                      `${blockName}__languages__language-button--available`
                    )}
                    key={`available-${language.code}`}
                    left
                    size="small"
                    clear
                  />
                  <SvgIcon
                    key={`add-${language.code}`}
                    customClassName={`${blockName}__languages__language-button__add`}
                    icon="CircleAddAlternate"
                    fillColor={colors.colorUIGood}
                  />
                </div>
              )
            )}
          </div>
        </div>
        {/*
          We subtract 1 here because languages includes the primary language,
          which does not need to be translated
        */}
        {(
          languages.size - 1 !==
          client.translatedLanguages.size
        ) && (
          <p className={classnames(
            `${blockName}__link-block`,
            "g-form__block__description",
            `${blockName}__description`
          )}
          >
            Add more languages in&nbsp;
            <Link
              to="/settings/languages"
              className={classnames(
                `${blockName}__link-block__link`,
                "g-form__block__description"
              )}
              onClick={() => this.props.setState("MODAL", {
                isOpen: false,
                view: null,
                size: null
              })}
            >
              Settings → Multilingual
            </Link>
          </p>
        )}
      </div>
    );
  }
}

function mapState(state) {
  const { responses } = state;

  return {
    client: getResource(state, "client"),
    languages: getResources(state, "language"),
    responses
  };
}

function mapDispatch(dispatch) {
  return bindActionCreators({ introsDeleteLanguage, setState }, dispatch);
}

MultilingualInputLanguagesDialog.propTypes = {
  activeLanguage: PropTypes.string,
  availableLanguages: ImmutablePropTypes.list,
  closeDialog: PropTypes.func,
  enabledLanguages: ImmutablePropTypes.list,
  onClick: PropTypes.func,
  onLanguageClick: PropTypes.func,
  deleteLanguage: PropTypes.func.isRequired,

  client: PropTypes.instanceOf(Client).isRequired,
  languages: ImmutablePropTypes.listOf(PropTypes.instanceOf(Language)).isRequired
};

const Connector = connect(mapState, mapDispatch)(MultilingualInputLanguagesDialog);

Connector.propTypes = {
  deleteLanguage: PropTypes.func.isRequired
};

export default Connector;
